import _ from 'lodash'

import { CommerceSalesOrder } from '@/models/api/commerce-sales-order'
import { log } from '@spa/log'
import { Order, OrderPurchaseFail } from '@spa/services/order'
import { CartItem } from '@spa/store/modules/cart/types'
import { Complete, OrderResult, OrderResultFail, OrderResultSuccess } from '@spa/store/modules/orderConfirm/types'

export default class CompleteDataBuilder {

  /**
   * すべて未指定時のデフォルトが定義できるものではない、
   * かつ、Complete構築のI/Fが変わったときにすべての利用元で
   * コンパイル時にエラーが発生する（修正を強制する）ことを目的としているため、
   * 意図的にすべての値をコンストラクタで受けるようにしている。
   */
  constructor(
    private commerceSalesOrders: CommerceSalesOrder[],
    private failedOrders: OrderPurchaseFail[],
    private cartItems: CartItem[],
    private sortResults: (results: OrderResult[]) => OrderResult[],
    private createOrderFromCartItems: (cartItems: CartItem[]) => Order[],
    private flags: {
      isFirstShareItem: boolean,
      isFreeShipping: boolean,
      canScheduleDelivery: boolean,
    }
  ) {}

  build(): Complete {
    return {
      orderResults: this.orderResults,
      bundleDiscountPrice: this.bundleDiscountPrice,
      couponDiscountPrice: this.couponDiscountPrice,
      isFirstShareItem: this.isFirstShareItem,
      isFreeShipping: this.isFreeShipping,
      canScheduleDelivery: this.canScheduleDelivery,
    }
  }

  private get orderResults(): OrderResult[] {
    const succeededOrdersResults = this.createSucceededOrderResults(
      this.commerceSalesOrders,
      this.cartItems
    )
    const failedOrderResults = this.createFailedOrderResults(this.failedOrders)

    return this.sortResults([...succeededOrdersResults, ...failedOrderResults])
  }

  private get bundleDiscountPrice(): number {
    return _.sumBy(this.commerceSalesOrders, o => o.bundleDiscountPrice)
  }

  private get couponDiscountPrice(): number {
    return _.sumBy(this.commerceSalesOrders, o => o.couponDiscountPrice)
  }

  private get isFirstShareItem(): boolean {
    return this.flags.isFirstShareItem
  }

  private get isFreeShipping(): boolean {
    return this.flags.isFreeShipping
  }

  private get canScheduleDelivery(): boolean {
    return this.flags.canScheduleDelivery
  }

  private createSucceededOrderResults(
    commerceSalesOrders: CommerceSalesOrder[],
    cartItemList: CartItem[]
  ): OrderResultSuccess[] {
    return _.compact(commerceSalesOrders.map((salesOrder) => {
      const ordersCartIds = salesOrder.salesOrderItems.map(i => i.cart_id)
      const cartItems = cartItemList.filter(item => ordersCartIds.indexOf(item.id) >= 0)

      const orders = this.createOrderFromCartItems(cartItems)

      // カートが空などの理由でorderが生成できなかった
      if (orders.length === 0) {
        log.error({
          service: 'order/complete-data-builder',
          message: 'Could not create any order objects from cart items.',
          orders,
          cartItems,
        })

        return null
      }

      // 事前にカート内商品を分類してから注文処理を行っている。
      // したがって、commerce_sales_orderで取得できるカート内商品を注文データとしてグルーピングすると、
      // 注文データは1件しか生成されないはず
      if (orders.length > 1) {
        log.error({
          service: 'order/complete-data-builder',
          message: 'must create single order because cart items already grouped before purchase',
          orders,
          cartItems,
        })

        return null
      }

      return {
        salesOrderId: salesOrder.id,
        commerceSalesOrder: salesOrder,
        order: orders[0],
        success: true,
        error: null,
      }
    }))
  }

  private createFailedOrderResults(orderPurchaseResults: OrderPurchaseFail[]): OrderResultFail[] {
    return orderPurchaseResults.map(result => ({
      ...result,
      commerceSalesOrder: null,
    }))
  }
}
